/* eslint-disable @typescript-eslint/no-explicit-any */ 

import { useEffect } from 'react';


import useDC from '@eneco/dc/useDC';
import Cookies from 'js-cookie';

import { usePageContent } from './usePageContent';
import { useFlowHooks } from '../utils/FlowProvider';
import { generateBasketIdCookieName, getBasketIdFromUrl } from '../utils/FlowUtils';

export const useFlowBasket = () => {
  const { useFlowSelector, useBasketSelector, useFlowActorRef, useBasketActorRef } = useFlowHooks();
  const flowState = useFlowSelector(state => state);
  const basketState = useBasketSelector(state => state);

  const sendToFlowMachine = useFlowActorRef().send;
  const sendToBasketMachine = useBasketActorRef().send;

  const { context: flowContext } = flowState;
  const { context: basketContext } = basketState;

  const { basketId, houseNumber, postalCode } = flowContext;

  const { flowName } = usePageContent();

  const { businessUnit, label } = useDC();

  // `String(...)` rather than `?? ''`: this has to produce the same cookie name
  // as useFlowBasketHelper's `${flowName?.toLowerCase()}_basket`, or a basket
  // written here would never be cleaned there. Both now yield 'undefined_basket'
  // on a page with no flowName, where this one used to throw on `.toLowerCase()`.
  const basketCookieName = generateBasketIdCookieName(String(flowName));

  useEffect(() => {
    if ((flowState as any).matches('IDLE')) {
      const savedBasketId = Cookies.get(basketCookieName);
      const basketIdFromUrl = getBasketIdFromUrl(basketCookieName);
      const basketId = savedBasketId || basketIdFromUrl;

      // check if flows context is already filled up (e.g. query params postalCode && houseNumber)
      // then prioritize that over a earlier filled up basket content
      sendToBasketMachine({
        type: basketId && !postalCode && !houseNumber ? 'GET_BASKET' : 'CREATE_BASKET',
        config: {
          businessUnit,
          label,
        },
        ...(basketId && { id: basketId }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowState]);

  useEffect(() => {
    // init flow with a earlier filled up basket context
    if ((basketState as any).matches('SUCCESS') || (basketState as any).matches('ERROR')) {
      const { errorMessage, ...valuesFiltered } = basketContext;
      sendToFlowMachine({ type: 'SET_ROUTE', values: valuesFiltered });
    }

    // if basket has status "CLOSED" we should set flow's state to "COMPLETED" as well
    if ((basketState as any).matches('CLOSED') && !(flowState as any).matches('COMPLETED')) {
      sendToFlowMachine({ type: 'COMPLETE' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basketState]);

  // each time flowMachines context changes we update the basket
  useEffect(() => {
    if (basketId && !(flowState as any).matches('INIT') && !(flowState as any).matches('IDLE')) {
      sendToBasketMachine({
        type: (flowState as any).matches('COMPLETED') ? 'CLOSE_BASKET' : 'UPDATE_BASKET',
        config: {
          businessUnit,
          label,
        },
        id: basketId,
        values: { ...flowContext },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basketId, flowContext]);

  useEffect(() => {
    if (basketId) {
      Cookies.set(basketCookieName, basketId);
    }
  }, [basketId, basketCookieName]);
};
